<template>
  <v-skeleton-loader
    v-if="isBreadcrumbsLoading"
    loading
    :height="40"
    width="30%"
    type="text"
    class="ml-n4"
  />
  <div v-else class="medium d-flex align-center">
    <span
      v-for="(route, index) in routes"
      :key="index"
      :class="`item-breadcrumb ${index === 0 ? 'heading-3' : 'body-1--new align-self-end'}`"
    >
      <router-link
        v-if="route.path && !route.navigate"
        class="breadcrumb-text text-decoration-none text-blue_600"
        :to="route.path"
      >
        {{ route.name }}
      </router-link>
      <!-- <button
        v-else
        class="brand_light--text breadcrumb-text text-decoration-none"
        @click="route.navigate"
      >
        {{ route.name }}
      </button> -->
    </span>
  </div>
</template>
<script setup lang="ts">
import { computed, watch, type ComputedRef } from 'vue'
import { type RouteRecordRaw } from 'vue-router'
import useGlobalStore from '@/stores/modules/global'
import { useNavigator } from '@/hooks'
import appRoutes from '@/router/routes'
import { memoizedExtractPaths } from '@/utils/helpers/app-helpers'
import {
  JOB_ADD_URL_NAME,
  JOB_DETAILS_URL_NAME,
  JOB_EDIT_URL_NAME,
  JOB_SCHEDULES_ADD_URL_NAME,
  JOB_SCHEDULES_EDIT_URL_NAME
} from '@/utils/constants'
import { JobLabelType, JobType } from '@/enum'
import useJobsStore from '@/stores/modules/jobs'

interface Breadcrumb {
  path: string
  name: string
  navigate: boolean
}

const navigator = useNavigator()
const globalStore = useGlobalStore()
const jobsStore = useJobsStore()
const currentRoute = computed(() => navigator.getCurrentRoute())

const jobType = computed(() => jobsStore.jobDetails.type)
const isBreadcrumbsLoading = computed(() => jobsStore.isLoading)
const routes: ComputedRef<Breadcrumb[]> = computed(() => {
  const currentPath: string = currentRoute.value.path.slice(1)
  const currentRouteName = currentRoute.value.name

  let breadcrumbRoutes: Breadcrumb[] = memoizedExtractPaths(currentPath)
    .filter((path) => {
      const matchedRoute: RouteRecordRaw | undefined = appRoutes.find(
        (route: RouteRecordRaw) =>
          route.path === path ||
          (route.name === currentRouteName && path.split('/').pop() === 'edit') ||
          (route.children &&
            !!route.children.filter((childRoute) => childRoute.path === path).length)
      )

      return !!matchedRoute?.path || !currentPath.includes('edit')
    })
    .map((path) => {
      let name = ''
      const routeFound = appRoutes.find(
        (route) =>
          route.path === path ||
          route.name === currentRouteName ||
          !!route.children?.filter((childRoute) => childRoute.path === path).length
      )
      if (!routeFound) {
        name = ''
      } else if (!routeFound.children) {
        // if no children
        name = routeFound.name as string
      } else {
        // otherwise matching with child routes
        name = routeFound.children.find((childRoute) => childRoute.path === path)?.name as string
      }
      if (
        routeFound?.name &&
        [
          JOB_ADD_URL_NAME,
          JOB_EDIT_URL_NAME,
          JOB_DETAILS_URL_NAME,
          JOB_SCHEDULES_ADD_URL_NAME,
          JOB_SCHEDULES_EDIT_URL_NAME
        ].includes(routeFound.name as string)
      ) {
        const { type } = currentRoute.value.query
        name = `${routeFound.name as string} - ${JobLabelType[JobType[type ? +type : (jobType.value ?? 0)] as keyof typeof JobLabelType]}`
      }
      return {
        path,
        name,
        navigate: false
      }
    })

  if (breadcrumbRoutes.length > 1) {
    const baseName: string = String(breadcrumbRoutes[0].name || '')
      ?.slice(0, -1)
      ?.trimEnd()

    breadcrumbRoutes = breadcrumbRoutes.map((breadcrumb: Breadcrumb, index): Breadcrumb => {
      if (!breadcrumb.name) {
        return {
          ...breadcrumb,
          name: `${baseName} details`
        }
      }
      return breadcrumb
    })
  }

  return breadcrumbRoutes
})

watch(routes, () => {
  if (routes.value.length > 1) {
    globalStore.hasBreadcrumbs = true
  } else {
    globalStore.hasBreadcrumbs = false
  }
})
</script>
<style scoped lang="scss">
@use '@/styles/variables';

.app-breadcrumbs {
  position: absolute;
  top: variables.$default-app-bar-height;
  width: 100%;
  height: variables.$default-breadcrumbs-height;
  z-index: 1;
}
.item-breadcrumb {
  padding-inline: 18px 12px;

  &:first-child {
    padding-left: 0;
  }
}
.item-breadcrumb:not(:first-child) {
  position: relative;
  &::before {
    content: '/';
    position: absolute;
    width: 6px;
    left: 0;
  }
}
.item-breadcrumb:last-child {
  pointer-events: none;

  .breadcrumb-text:last-child {
    color: rgb(var(--v-theme-slate_500)) !important;
  }
}
</style>
